import React, { ReactNode, Suspense, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useLocation } from 'react-router';

interface SuspenseAndErrorBoundaryProps {
  children: ReactNode;
  notFoundMessage?: string;
  onReset?: () => void;
  fallbackRender?: (props: FallbackProps) => ReactNode;
}

// Enhanced Error Fallback component with retry functionality
const ErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  return (
    <Box 
      textAlign="center" 
      mt={4} 
      display="flex" 
      flexDirection="column" 
      gap={2}
      alignItems="center"
    >
      <Typography variant="h6" color="error">
        Something went wrong. Please try again.
      </Typography>
      {process.env.NODE_ENV === 'development' && (
        <Typography variant="body2" color="textSecondary" sx={{ maxWidth: '600px', wordBreak: 'break-word' }}>
          {error.message}
        </Typography>
      )}
      <button
        onClick={resetErrorBoundary}
        style={{
          padding: '8px 16px',
          backgroundColor: '#1976d2',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        Try Again
      </button>
    </Box>
  );
};

// Loading component
const LoadingFallback: React.FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
    <CircularProgress />
  </Box>
);

// Enhanced Wrapper Component
export const SuspenseAndErrorBoundary: React.FC<SuspenseAndErrorBoundaryProps> = ({
  children,
  notFoundMessage = "Data not found",
  onReset,
  fallbackRender,
}) => {
  const location = useLocation();
  const [key, setKey] = React.useState(location.pathname);

  // Update key when location changes to reset error boundary
  useEffect(() => {
    setKey(location.pathname);
  }, [location.pathname]);

  const handleReset = () => {
    onReset?.();
  };

  return (
    <ErrorBoundary
      FallbackComponent={fallbackRender ?? ErrorFallback}
      onReset={handleReset}
      key={key} // This ensures the error boundary resets when the path changes
    >
      <Suspense fallback={<LoadingFallback />}>
        {children || (
          <Box textAlign="center" mt={4}>
            <Typography variant="h6" color="textSecondary">
              {notFoundMessage}
            </Typography>
          </Box>
        )}
      </Suspense>
    </ErrorBoundary>
  );
};

// Optional: Export a hook to create test errors (development only)
export const useTestError = () => {
  const throwError = () => {
    throw new Error('Test error thrown');
  };

  return { throwError };
};